import { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import {
  FamilyMemberFormType,
  familyMemberToFormType,
} from "@utils/patientFormHelper";
import {
  OnboardingFormActionContainer,
  OnboardingFormContainer,
} from "@components/Onboarding/OnboardingForm";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { Patient } from "@models/patient";
import {
  useGetPatientQuery,
  useUpdatePatientMutation,
} from "@store/patientApi";
import { setMessage } from "@store/snackbarSlice";
import FamilyMemberForm from "@components/FamilyMemberForm/FamilyMemberForm";
import TextIconButton from "@components/TextIconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const OnboardingFamilyMemberForm: FC = () => {
  const { t } = useTranslation();
  const { familyMemberId } = useParams();
  const patientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const navigate = useNavigate();
  const { data: patient } = useGetPatientQuery(patientId!, {
    skip: !patientId,
  });
  const familyMember = useMemo(
    () => patient?.familyMembers.find(({ id }) => id === familyMemberId),
    [patient, familyMemberId],
  );
  const [updatePatient] = useUpdatePatientMutation();
  const dispatch = useDispatch();

  const form = useForm<FamilyMemberFormType>();
  const {
    reset,
    handleSubmit,
    clearErrors,
    formState: { isSubmitting },
  } = form;

  useEffect(() => {
    if (familyMember) {
      reset(familyMemberToFormType(familyMember));
    }
  }, [familyMember, reset]);

  const save = async (values: FamilyMemberFormType): Promise<void> => {
    try {
      const { data, error } = await updatePatient({
        id: patient?.id,
        familyMembers: [
          ...(patient?.familyMembers?.filter(
            (fm) => fm.id !== familyMemberId,
          ) || []),
          { ...values },
        ],
      } as Patient);
      if (data && !error) {
        navigate("../");
      } else {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      }
    } catch (e) {
      dispatch(setMessage({ content: t(`errors.unknown`), severity: "error" }));
    }
  };

  const goBack = () => {
    navigate("../");
  };

  return (
    <Stack>
      <Box marginBottom="32px">
        <TextIconButton
          onClick={goBack}
          text={t("backButton")}
          icon={ArrowBackIcon}
          variant="text"
          iconLeft={true}
        />
      </Box>

      <OnboardingFormContainer
        onSubmit={(e) => {
          clearErrors();
          return handleSubmit(save)(e);
        }}
      >
        <FamilyMemberForm form={form} />

        <OnboardingFormActionContainer>
          <Box marginLeft="auto">
            <Button
              type="submit"
              disabled={isSubmitting}
              variant="contained"
              size="large"
            >
              {t(`familyInfo.form.saveButton`)}
            </Button>
          </Box>
        </OnboardingFormActionContainer>
      </OnboardingFormContainer>
    </Stack>
  );
};

export default OnboardingFamilyMemberForm;
